import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, Divider, Typography } from '@mui/material';
import background from '../assets/background.png';
import youcoin from '../assets/youcoin.png';
import star from '../assets/star.png';
import { getAllOpportunities, ACCESS_TOKEN_KEY } from '../api';
import { Opportunity as OpportunityType, OpportunityResponse, LogoAnimationSettings } from '../types';
import { useNavigate } from 'react-router-dom';
import AppBarOverlay from '../components/AppBar';
import { parseUrlParams } from '../utils/urlParams';
import logoAnimationService from '../services/logoAnimationService';

type OppState = "tagged" | "created";

const parsePositiveInt = (value: string | null | undefined, fallback: number) => {
  const count = parseInt(value || '', 10);
  return !isNaN(count) && count > 0 ? count : fallback;
};

const parseOpacity = (value: string | null | undefined) => {
  const opacity = parseFloat(value || '');
  return Number.isFinite(opacity) ? Math.min(1, Math.max(0, opacity)) : 1;
};

const readOppState = (): OppState => {
  const urlState = new URLSearchParams(window.location.search).get('state');
  const storedState = localStorage.getItem('current_opps_state');
  if (urlState === 'tagged' || urlState === 'created') {
    return urlState;
  }
  if (storedState === 'tagged' || storedState === 'created') {
    return storedState;
  }
  return 'tagged';
};

const rememberOppState = (state: OppState) => {
  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get('state') !== state) {
    currentUrl.searchParams.set('state', state);
    window.history.replaceState({}, '', currentUrl.toString());
  }
  localStorage.setItem('current_opps_state', state);
};

const Opps = () => {
  const navigate = useNavigate();
  const [oppsCache, setOppsCache] = useState<Record<OppState, OpportunityType[]>>({
    tagged: [],
    created: [],
  });
  const [gettingOpps, setGettingOpps] = useState(false);
  const [error, setError] = useState('');
  const [oppShown, setOppShown] = useState<OppState>(readOppState);
  const [shortCount, setShortCount] = useState(5);
  const [extendedCount, setExtendedCount] = useState(10);
  const [overlaySize, setOverlaySize] = useState({ width: '800px', height: '600px' });
  const [overlayOpacity, setOverlayOpacity] = useState(1);
  const [logoAnimationSettings, setLogoAnimationSettings] = useState<LogoAnimationSettings>({
    enabled: false,
    frequency: 60,
    duration: 10,
    textTemplate: '{{sender}} donated {{amount}} for {{challenge}}'
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);

  const oppShownRef = useRef(oppShown);
  const oppsCacheRef = useRef(oppsCache);
  const settingsReadyRef = useRef(false);
  const refreshTimerRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);

  oppShownRef.current = oppShown;
  oppsCacheRef.current = oppsCache;

  const isLogoAnimationEnabled = logoAnimationSettings.enabled;
  const isExpanded = !isLogoAnimationEnabled || isAnimating || isCollapsing;
  const collapseTransitionMs = 500;
  const fullContentOpacity = !isLogoAnimationEnabled ? 1 : isAnimating && !isCollapsing ? 1 : 0;
  const logoContentOpacity = isLogoAnimationEnabled && !isAnimating && !isCollapsing ? 1 : 0;
  const visibleCount = isLogoAnimationEnabled && isAnimating && !isCollapsing ? extendedCount : shortCount;

  const currentOverlaySize = isLogoAnimationEnabled && !isExpanded
    ? { width: '120px', height: '120px', minHeight: undefined as string | undefined, maxHeight: undefined as string | undefined }
    : {
        width: overlaySize.width,
        height: 'auto',
        minHeight: '200px',
        maxHeight: overlaySize.height,
      };

  const applyOverlaySettings = () => {
    setOverlaySize({
      width: localStorage.getItem('overlay_width') || '800px',
      height: localStorage.getItem('overlay_height') || '600px',
    });
    setOverlayOpacity(parseOpacity(localStorage.getItem('overlay_opacity') || '1'));
    setShortCount(parsePositiveInt(localStorage.getItem('overlay_show_count'), 5));
    setExtendedCount(parsePositiveInt(localStorage.getItem('overlay_show_count_extended'), 10));
  };

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      window.clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const getOpps = async () => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const fetchState = oppShownRef.current;
    const hasCachedOpps = (oppsCacheRef.current[fetchState]?.length ?? 0) > 0;

    if (!token) {
      setError('No authentication token found');
      setGettingOpps(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    if (!hasCachedOpps) {
      setGettingOpps(true);
    }
    setError('');

    try {
      const res: OpportunityResponse = await getAllOpportunities({ type: fetchState });

      if (res.code === 20000) {
        const nextOpps = Array.isArray(res.opps) ? res.opps : [];
        setOppsCache((prev) => ({
          ...prev,
          [fetchState]: nextOpps,
        }));
        if (
          requestId === requestIdRef.current &&
          nextOpps.length === 0 &&
          !hasCachedOpps &&
          oppShownRef.current === fetchState
        ) {
          setError('No Opportunities found');
        }
      } else if (requestId === requestIdRef.current && oppShownRef.current === fetchState && !hasCachedOpps) {
        setError(res.code === 40100 ? 'Unauthorized - please check your token' : 'No Opportunities found');
      }
    } catch (loadError) {
      console.error('Error in getOpps:', loadError);
      if (requestId === requestIdRef.current && oppShownRef.current === fetchState && !hasCachedOpps) {
        setError('Error loading opportunities');
      }
    }
    if (requestId === requestIdRef.current) {
      setGettingOpps(false);
    }
  };

  const getOppsRef = useRef(getOpps);
  getOppsRef.current = getOpps;

  const scheduleRefresh = useCallback((seconds: number) => {
    clearRefreshTimer();
    if (seconds > 0) {
      refreshTimerRef.current = window.setInterval(() => {
        void getOppsRef.current();
      }, seconds * 1000);
    }
  }, [clearRefreshTimer]);
  const scheduleRefreshRef = useRef(scheduleRefresh);
  scheduleRefreshRef.current = scheduleRefresh;

  const showOtherList = () => {
    const nextState: OppState = oppShown === "tagged" ? "created" : "tagged";
    rememberOppState(nextState);
    setOppShown(nextState);
  };

  useEffect(() => {
    let cancelled = false;

    const loadUrlParams = async () => {
      const urlParams = await parseUrlParams();
      if (cancelled) {
        return;
      }

      rememberOppState(oppShownRef.current);

      if (urlParams.access_token) {
        localStorage.setItem(ACCESS_TOKEN_KEY, urlParams.access_token);
      }
      if (urlParams.width) localStorage.setItem('overlay_width', urlParams.width);
      if (urlParams.height) localStorage.setItem('overlay_height', urlParams.height);
      if (urlParams.opacity) localStorage.setItem('overlay_opacity', urlParams.opacity);
      if (urlParams.show_count) localStorage.setItem('overlay_show_count', urlParams.show_count);
      if (urlParams.show_count_extended) {
        localStorage.setItem('overlay_show_count_extended', urlParams.show_count_extended);
      }
      if (urlParams.refresh_frequency) {
        localStorage.setItem('overlay_refresh_frequency', urlParams.refresh_frequency);
      }

      applyOverlaySettings();
      if (urlParams.opacity) {
        setOverlayOpacity(parseOpacity(urlParams.opacity));
      }

      const animationSettings = logoAnimationService.getSettings();
      const frequency = parsePositiveInt(urlParams.animation_frequency, animationSettings.frequency);
      const duration = parsePositiveInt(urlParams.animation_duration, animationSettings.duration);
      setLogoAnimationSettings({
        enabled: urlParams.animation_enabled ? urlParams.animation_enabled === 'true' : animationSettings.enabled,
        frequency,
        duration,
        textTemplate: urlParams.animation_text_template || animationSettings.textTemplate,
      });

      scheduleRefreshRef.current(parsePositiveInt(
        urlParams.refresh_frequency || localStorage.getItem('overlay_refresh_frequency'),
        0,
      ));

      settingsReadyRef.current = true;
      await getOppsRef.current();
    };

    void loadUrlParams();

    return () => {
      cancelled = true;
      clearRefreshTimer();
    };
  }, [clearRefreshTimer]);

  useEffect(() => {
    if (!settingsReadyRef.current) {
      return;
    }
    void getOppsRef.current();
  }, [oppShown]);

  useEffect(() => {
    const handlePopState = () => {
      const nextState = readOppState();
      setOppShown(nextState);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!logoAnimationSettings.enabled) {
      setIsAnimating(false);
      setIsCollapsing(false);
      return;
    }

    let activeTimeouts: Array<ReturnType<typeof setTimeout>> = [];
    const clearActiveTimeouts = () => {
      activeTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      activeTimeouts = [];
    };

    const startAnimation = () => {
      clearActiveTimeouts();
      const currentState = oppShownRef.current;
      setIsCollapsing(false);
      setIsAnimating(true);

      const halfwayTime = (logoAnimationSettings.duration * 1000) / 2;
      activeTimeouts.push(setTimeout(() => {
        const nextState: OppState = currentState === "tagged" ? "created" : "tagged";
        rememberOppState(nextState);
        setOppShown(nextState);
      }, halfwayTime));

      activeTimeouts.push(setTimeout(() => {
        setIsCollapsing(true);
        activeTimeouts.push(setTimeout(() => {
          setIsAnimating(false);
          setIsCollapsing(false);
          rememberOppState(currentState);
          setOppShown(currentState);
        }, collapseTransitionMs));
      }, logoAnimationSettings.duration * 1000));
    };

    startAnimation();
    const interval = window.setInterval(
      startAnimation,
      Math.max(logoAnimationSettings.frequency, 1) * 1000,
    );

    return () => {
      window.clearInterval(interval);
      clearActiveTimeouts();
    };
  }, [logoAnimationSettings.enabled, logoAnimationSettings.frequency, logoAnimationSettings.duration]);

  useEffect(() => {
    const handleStorageChange = () => {
      applyOverlaySettings();
      scheduleRefreshRef.current(parsePositiveInt(localStorage.getItem('overlay_refresh_frequency'), 0));
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('overlaySettingsChanged', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('overlaySettingsChanged', handleStorageChange);
    };
  }, []);

  const displayedOpps = Array.isArray(oppsCache[oppShown]) ? oppsCache[oppShown] : [];

  return (
    <div
      style={{
        overflow: isLogoAnimationEnabled && !isExpanded ? 'hidden' : 'visible',
        display: 'flex',
        flexDirection: 'column',
        width: currentOverlaySize.width,
        maxWidth: '100%',
        height: currentOverlaySize.height,
        minHeight: currentOverlaySize.minHeight,
        maxHeight: currentOverlaySize.maxHeight,
        opacity: overlayOpacity,
        textAlign: 'center',
        borderRadius: isLogoAnimationEnabled && !isExpanded ? 15 : 30,
        position: 'relative',
        transition: `width ${collapseTransitionMs}ms ease-in-out, height ${collapseTransitionMs}ms ease-in-out, min-height ${collapseTransitionMs}ms ease-in-out, max-height ${collapseTransitionMs}ms ease-in-out, border-radius ${collapseTransitionMs}ms ease-in-out`,
        backgroundColor: isLogoAnimationEnabled && !isExpanded ? 'transparent' : undefined,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${background})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: fullContentOpacity,
          transition: `opacity ${collapseTransitionMs}ms ease-in-out`,
          pointerEvents: 'none',
          borderRadius: 'inherit',
        }}
      />

      {isLogoAnimationEnabled && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            padding: 10,
            zIndex: 2,
            opacity: logoContentOpacity,
            transition: `opacity ${collapseTransitionMs}ms ease-in-out`,
            pointerEvents: logoContentOpacity > 0 ? 'auto' : 'none',
          }}
        >
          <img
            src={star}
            alt="YouCould"
            style={{
              maxWidth: '100px',
              maxHeight: '100px',
              objectFit: 'contain',
            }}
          />
        </div>
      )}

      <div
        style={{
          position: isLogoAnimationEnabled && !isExpanded ? 'absolute' : 'relative',
          top: isLogoAnimationEnabled && !isExpanded ? 0 : undefined,
          left: isLogoAnimationEnabled && !isExpanded ? 0 : undefined,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          opacity: fullContentOpacity,
          transition: `opacity ${collapseTransitionMs}ms ease-in-out`,
          pointerEvents: fullContentOpacity > 0 ? 'auto' : 'none',
          visibility: fullContentOpacity > 0 ? 'visible' : 'hidden',
        }}
      >
        <AppBarOverlay
          oppsShownButtonAction={showOtherList}
          navigateTo={() => navigate('/settings')}
          oppShown={{ state: oppShown }}
        />

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          marginTop: 10,
          padding: '0 12px 10px',
          boxSizing: 'border-box',
          gap: 4,
        }}>
          {gettingOpps && displayedOpps.length === 0 ? (
            <Typography variant="h6" color="white">Loading...</Typography>
          ) : error && displayedOpps.length === 0 ? (
            <Typography variant="h6" color="white">{error}</Typography>
          ) : displayedOpps.length === 0 ? (
            <Typography variant="h6" color="white">No Opportunities found</Typography>
          ) : (
            displayedOpps.slice(0, visibleCount).map((opp, index) => (
              <Fragment key={opp.id || `${opp.head_line}-${index}`}>
                <Card style={{ width: '100%', maxWidth: 600, backgroundColor: 'transparent', boxShadow: 'none' }}>
                  <CardContent style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    backgroundColor: 'transparent',
                    borderRadius: 10,
                    padding: '6px 4px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" style={{ color: 'white', marginRight: 10, flexShrink: 0 }}>{index + 1}.</Typography>
                      <Typography variant="h6" style={{ textAlign: 'left', color: 'white', overflowWrap: 'anywhere' }}>
                        {opp.head_line || 'Untitled challenge'}
                      </Typography>
                    </div>
                    <Typography variant="h6" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', flexShrink: 0, color: 'white' }}>
                      {opp.reward?.ucoin ?? 0}
                      <img src={youcoin} alt="" style={{ marginLeft: 5, maxHeight: 20 }} />
                    </Typography>
                  </CardContent>
                </Card>
                {index < visibleCount - 1 && index < displayedOpps.length - 1 && (
                  <Divider style={{ width: '100%', maxWidth: 640, backgroundColor: 'gray' }} />
                )}
              </Fragment>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Opps;
